import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";

import {
  ForecastTuningPolicyEntry,
  useUpdateForecastTuningPolicy,
} from "./use-forecast-tuning";

interface Props {
  entry: ForecastTuningPolicyEntry;
}

/**
 * Bloque D.8 — form item por entry de `forecast_tuning_policy`. Soporta
 * `value_numeric` (input number con validación min/max visual) y
 * `value_text` (input string para cron expr). El JSON queda fuera por ahora
 * (no aplicable en los 16 keys seedeados).
 */
export function ForecastTuningForm({ entry }: Props): React.ReactElement {
  const isNumeric = entry.value_numeric !== null;
  const isText = entry.value_text !== null;
  const initialValue = isNumeric
    ? entry.value_numeric ?? ""
    : isText
      ? entry.value_text ?? ""
      : "";
  const [value, setValue] = useState<string>(String(initialValue));
  const [feedback, setFeedback] = useState<
    | { kind: "success"; message: string }
    | { kind: "error"; message: string }
    | null
  >(null);
  const mutation = useUpdateForecastTuningPolicy();

  const min = entry.min_value !== null ? Number(entry.min_value) : null;
  const max = entry.max_value !== null ? Number(entry.max_value) : null;
  const numValue = isNumeric ? Number(value) : NaN;
  const isOutOfRange =
    isNumeric &&
    Number.isFinite(numValue) &&
    ((min !== null && numValue < min) || (max !== null && numValue > max));

  async function onSave() {
    setFeedback(null);
    try {
      if (isNumeric) {
        if (!Number.isFinite(numValue)) {
          setFeedback({ kind: "error", message: "Valor inválido" });
          return;
        }
        await mutation.mutateAsync({ key: entry.key, value_numeric: numValue });
      } else {
        await mutation.mutateAsync({ key: entry.key, value_text: value });
      }
      setFeedback({ kind: "success", message: "Guardado" });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error";
      setFeedback({ kind: "error", message: msg });
    }
  }

  return (
    <Box sx={{ p: 2, border: "1px solid", borderColor: "divider", mb: 1 }}>
      <Stack spacing={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          {entry.key}
          {entry.unit && (
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              ({entry.unit})
            </Typography>
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {entry.description}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            type={isNumeric ? "number" : "text"}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            error={isOutOfRange}
            helperText={
              isOutOfRange
                ? `Fuera de rango (min=${min ?? "—"}, max=${max ?? "—"})`
                : isNumeric && (min !== null || max !== null)
                  ? `Rango: ${min ?? "—"} … ${max ?? "—"}`
                  : ""
            }
            inputProps={isNumeric ? { min, max } : undefined}
          />
          <Button
            size="small"
            variant="contained"
            onClick={onSave}
            disabled={mutation.isPending || isOutOfRange}
          >
            Guardar
          </Button>
        </Stack>
        {feedback && (
          <Alert severity={feedback.kind} variant="outlined">
            {feedback.message}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
